// Typed wrapper for the locales.json manifest.
//
// `resolveJsonModule: true` in tsconfig preserves the JSON object's literal
// key set, so `keyof typeof raw` gives the exact 16-code union rather than
// widening to `string`. The FF4 type test in manifest.test.ts asserts this.
//
// Display order: English first, then alphabetical by locale code — mirrors
// the LOCALES array in i18n/index.ts (no order field in JSON; order is
// always derivable from the key set with the same sort rule).

import raw from './locales.json';

export interface LocaleMeta {
	label: string;
	native: string;
	og: string;
	rtl: boolean;
	default?: boolean;
}

// Literal-key inference: resolveJsonModule preserves the key set of the JSON
// object, so keyof gives the exact 16-code union (FF4 asserts this).
export type Locale = keyof typeof raw;

export const manifest: Record<Locale, LocaleMeta> = raw;

// Display order: English first, then alphabetical by locale code.
// Byte comparator avoids ICU collation question entirely — locale codes are
// ASCII-only identifiers, so code-point order is the correct sort.
export const manifestLocales: readonly Locale[] = Object.freeze(
	(Object.keys(raw) as Locale[]).sort((a, b) =>
		a === 'en' ? -1 : b === 'en' ? 1 : a < b ? -1 : a > b ? 1 : 0,
	),
);

export const manifestLabels: Record<Locale, string> = Object.fromEntries(
	(Object.entries(raw) as [Locale, LocaleMeta][]).map(([k, v]) => [k, v.label]),
) as Record<Locale, string>;

export const manifestNative: Record<Locale, string> = Object.fromEntries(
	(Object.entries(raw) as [Locale, LocaleMeta][]).map(([k, v]) => [k, v.native]),
) as Record<Locale, string>;

export const manifestOg: Record<Locale, string> = Object.fromEntries(
	(Object.entries(raw) as [Locale, LocaleMeta][]).map(([k, v]) => [k, v.og]),
) as Record<Locale, string>;

export const manifestRtl: ReadonlySet<Locale> = new Set(
	(Object.entries(raw) as [Locale, LocaleMeta][])
		.filter(([, v]) => v.rtl)
		.map(([k]) => k),
);

// Semantic default locale for an undetermined visitor — the locale used when
// the browser and explicit overrides fail to resolve. Derived from the
// single locales.json entry carrying `"default": true`. The manifest.test.ts
// guard asserts exactly one such entry exists.
//
// There is no eager-bundle locale anymore: since lazy-ru every locale
// (ru included) is a lazy chunk in translations/index.ts. Keep DEFAULT_LOCALE
// distinct from whatever the first paint happens to render — the semantic
// default is `en`, the prerendered body copy is `ru`, and conflating them is
// what caused the server/client drift.
export const DEFAULT_LOCALE: Locale = (
	(Object.entries(raw) as [Locale, LocaleMeta][]).find(([, v]) => v.default) ?? ['en', raw.en]
)[0] as Locale;
